import { forwardRef } from "react";
import type { Strings } from "../i18n/strings";
import { ARCHETYPE_NAME } from "../i18n/strings";
import { archetypeColor } from "../lib/archetypeMeta";
import { useCountUp } from "../lib/useMotion";
import type { AngerCurve, Archetype, Lang } from "../lib/types";
import Logo from "./Logo";
import AngerCurveViz from "./AngerCurveViz";
import TypeArt from "./TypeArt";
import RayBurst from "./RayBurst";

// 공유 카드(9:16) 비주얼 — ShareCard(기기)와 CardRender(서버 영상)가 공유하는 단일 출처.
// ★원문 텍스트는 받지 않는다. 표시에 필요한 파생 수치(words·exclaims·charCount)만 받는다(무흔적).
export interface CardData {
  lang: Lang;
  archetype: Archetype;
  score: number;
  headline: string;
  curve: AngerCurve;
  words: number;
  exclaims: number;
  charCount: number;
}

function clock(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

interface Props {
  t: Strings;
  data: CardData;
  /** true면 등장+내부 그리기 애니메이션 재생(영상/첫 진입). false면 정적. */
  animate?: boolean;
}

// stage div에 ref를 전달한다(ShareCard의 html-to-image 캡처용).
const ResultCard = forwardRef<HTMLDivElement, Props>(function ResultCard({ t, data, animate = true }, ref) {
  const { lang, archetype, score, headline, curve } = data;
  const color = archetypeColor(archetype);
  const name = ARCHETYPE_NAME[archetype][lang];
  const rank = 1 + ((score * 137 + 911) % 9000);
  const space = lang === "ja" ? "" : " ";
  const statMeta =
    lang === "ja"
      ? `${data.charCount}文字 · ！${data.exclaims}`
      : lang === "ko"
        ? `${data.words}단어 · 느낌표 ${data.exclaims}`
        : `${data.words}W · ${data.exclaims}!`;
  const shown = useCountUp(score, animate ? 1100 : 0);

  return (
    <div className="card-stage" ref={ref}>
      <RayBurst color={color} opacity={0.18} seed={score + 3} />
      <div className="card" style={{ borderColor: color }}>
        <div className="card-top">
          <Logo lang={lang} height={22} stroke={30} />
          <span className="card-meta">
            {clock()} · {t.metaA}
            {space}
            {rank.toLocaleString()}
            {t.metaB}
          </span>
        </div>
        <div className="card-mid">
          <TypeArt typeId={archetype} accent={color} ink="#fff" size={96} sw={3.2} draw={animate} />
          <div className="kicker" style={{ color }}>
            {t.kicker}
          </div>
          <div className="card-typename">{name}</div>
          <div className="card-score-row">
            <span className="card-score" style={{ color }}>
              {shown}
            </span>
            <span className="card-score-suffix">/100</span>
          </div>
          <p className="card-headline">“{headline}”</p>
        </div>
        <div className="card-bottom">
          <AngerCurveViz curve={curve} color={color} w={260} h={54} showAxis={false} animate={animate} />
          <div className="card-foot">
            <span className="card-statmeta">{statMeta}</span>
            <span className="card-brand" style={{ color }}>
              {t.brand}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ResultCard;
